import { useId, useRef, useState } from 'react';
import api from '../api';
import { ReactComponent as AddIcon } from '../assets/images/icons/ic-add.svg';
import { ReactComponent as EditIcon } from '../assets/images/icons/ic-edit.svg';
import { ReactComponent as DeleteIcon } from '../assets/images/icons/ic-delete.svg';
import { ReactSortable } from 'react-sortablejs';
import { ReactComponent as ReorderIcon } from '../assets/images/icons/ic-reorder.svg';
import { groupEventSponsors } from '../utils/eventSponsorOrder';
import { FileType } from '../common/constants';
import Button from './Button';
import Card from './cards/Card';
import Dialog from './dialogs/Dialog';
import Uploader from './Uploader';
import styles from './EventSponsors.module.css';

const validImage = (value) => {
    try { return ['http:', 'https:'].includes(new URL(value).protocol); }
    catch { return false; }
};

export default function EventSponsors({ event, onSaved }) {
    const [draft, setDraft] = useState(null);
    const [ordering, setOrdering] = useState(false);
    const [orderDraft, setOrderDraft] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const categoryListId = useId();
    const scrollRef = useRef(null);
    const sponsors = Array.isArray(event.sponsors) ? event.sponsors : [];
    const groups = ordering ? orderDraft : groupEventSponsors(sponsors).map(group => ({
        ...group,
        id: group.category,
        sponsors: group.sponsors.map(item => ({ id: sponsors.indexOf(item), item })),
    }));
    const categories = groups.map((group) => group.category);
    const valid = draft?.category.trim() && draft?.name.trim() && validImage(draft?.img.trim());

    const persist = async (next) => {
        setSaving(true);
        setError('');
        try {
            const updated = await api.put(`/admin/events/${event.id}`, { sponsors: next });
            onSaved(updated.sponsors);
            setDraft(null);
            setOrdering(false);
            setOrderDraft([]);
        } catch {
            setError('Impossibile salvare gli sponsor. Riprova.');
        } finally {
            setSaving(false);
        }
    };

    const save = () => {
        const item = { category: draft.category.trim(), name: draft.name.trim(), img: draft.img.trim() };
        const next = [...sponsors];
        if (draft.index === null) next.push(item);
        else next[draft.index] = item;
        persist(groupEventSponsors(next, categories).flatMap((group) => group.sponsors));
    };

    const reorderGroups = next => {
        if (!ordering || saving) return;
        setOrderDraft(previous => next.some((group, index) => group.id !== previous[index]?.id) ? next : previous);
    };

    const reorderSponsors = (category, next) => {
        if (!ordering || saving) return;
        setOrderDraft(previous => {
            const current = previous.find(group => group.id === category);
            if (!current || !next.some((row, index) => row.id !== current.sponsors[index]?.id)) return previous;
            return previous.map(group => group.id === category ? { ...group, sponsors: next } : group);
        });
    };

    const moveWithKeyboard = (event, items, index, onChange) => {
        if (saving || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        const target = index + (event.key === 'ArrowUp' ? -1 : 1);
        if (target < 0 || target >= items.length) return;
        const next = [...items];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <Card appearance="modern" title="Sponsor evento">
            <div className={styles.content}>
                <div className={styles.toolbar}>
                    <span className={styles.summary} role="status">{saving ? 'Salvataggio…' : `${sponsors.length} sponsor · ${groups.length} categorie`}</span>
                    <div className={styles.actions}>
                        {ordering ? <>
                            <Button appearance="subtle" requiresEdit disabled={saving}
                                onClick={() => persist(orderDraft.flatMap(group => group.sponsors.map(row => row.item)))}>
                                {saving ? 'Salvataggio…' : 'Salva'}
                            </Button>
                            <Button appearance="subtle" disabled={saving} onClick={() => { setOrdering(false); setOrderDraft([]); setError(''); }}>Annulla</Button>
                        </> : <>
                            {sponsors.length > 1 && <Button appearance="subtle" additionalClass={styles.toolbarButton} requiresEdit
                                disabled={saving} onClick={() => { setError(''); setOrderDraft(groups); setOrdering(true); }}>
                                <ReorderIcon aria-hidden="true" /> Ordina
                            </Button>}
                            <Button appearance="subtle" additionalClass={styles.toolbarButton} requiresEdit disabled={saving}
                                onClick={() => { setError(''); setDraft({ index: null, category: '', name: '', img: '' }); }}>
                                <AddIcon aria-hidden="true" /> Aggiungi
                            </Button>
                        </>}
                    </div>
                </div>
                {ordering && <p className={styles.hint}>Trascina l’handle della categoria per spostare tutto il gruppo, oppure quello dello sponsor per riordinarlo nella categoria. Premi Salva per confermare. Puoi anche usare le frecce su e giù sugli handle.</p>}
                {!draft && error && <p className={styles.error} role="alert">{error}</p>}
                {sponsors.length === 0 && <p className={styles.empty}>Aggiungi il primo sponsor dell’evento.</p>}
                <div className={styles.scrollArea} ref={scrollRef}>
                <ReactSortable list={groups} setList={reorderGroups} disabled={!ordering || saving}
                    handle={`.${styles.groupHandle}`} animation={180} ghostClass={styles.dragGhost}
                    scroll={scrollRef.current || true} bubbleScroll={false} scrollSensitivity={60} scrollSpeed={12}>
                {groups.map((group, categoryIndex) => (
                    <section className={styles.category} key={group.category} aria-label={group.category}>
                        <div className={styles.categoryHeader}>
                            {ordering && <button type="button" className={`${styles.dragHandle} ${styles.groupHandle}`} disabled={saving}
                                aria-label={`Sposta categoria ${group.category}`} title="Trascina il gruppo o usa le frecce su e giù"
                                onKeyDown={event => moveWithKeyboard(event, groups, categoryIndex, reorderGroups)}><ReorderIcon aria-hidden="true" /></button>}
                            <h3>{group.category}<span className={styles.count}>{group.sponsors.length}</span></h3>
                            <div className={styles.actions}>
                                {!ordering && <Button appearance="subtle" additionalClass={styles.iconButton} requiresEdit disabled={saving}
                                    aria-label={`Aggiungi sponsor a ${group.category}`} title="Aggiungi sponsor"
                                    onClick={() => { setError(''); setDraft({ index: null, category: group.category, name: '', img: '' }); }}>
                                    <AddIcon aria-hidden="true" />
                                </Button>}
                            </div>
                        </div>
                        <ReactSortable list={group.sponsors} setList={next => reorderSponsors(group.id, next)}
                            disabled={!ordering || saving} handle={`.${styles.sponsorHandle}`} animation={180}
                            ghostClass={styles.dragGhost} scroll={scrollRef.current || true} bubbleScroll={false} scrollSensitivity={60} scrollSpeed={12}>
                        {group.sponsors.map((row, sponsorIndex) => {
                            const { item } = row;
                            const index = sponsors.indexOf(item);
                            return (
                                <div className={styles.row} key={row.id}>
                                    {ordering && <button type="button" className={`${styles.dragHandle} ${styles.sponsorHandle}`} disabled={saving}
                                        aria-label={`Sposta ${item.name} in ${group.category}`} title="Trascina lo sponsor o usa le frecce su e giù"
                                        onKeyDown={event => moveWithKeyboard(event, group.sponsors, sponsorIndex, next => reorderSponsors(group.id, next))}><ReorderIcon aria-hidden="true" /></button>}
                                    <div className={styles.logoFrame}><img className={styles.logo} src={item.img} alt="" /></div>
                                    <div className={styles.name}><strong>{item.name}</strong></div>
                                    <div className={styles.actions}>
                                        {!ordering && <>
                                        <Button appearance="subtle" additionalClass={styles.iconButton} requiresEdit disabled={saving} title="Modifica sponsor" aria-label={`Modifica ${item.name}`} onClick={() => { setError(''); setDraft({ ...item, index }); }}><EditIcon aria-hidden="true" /></Button>
                                        <Button appearance="subtle" additionalClass={`${styles.iconButton} ${styles.deleteButton}`} requiresEdit disabled={saving} title="Elimina sponsor" aria-label={`Elimina ${item.name}`} onClick={() => {
                                            if (window.confirm(`Eliminare lo sponsor ${item.name}?`)) persist(groupEventSponsors(sponsors.filter((_, i) => i !== index), categories).flatMap((entry) => entry.sponsors));
                                        }}><DeleteIcon aria-hidden="true" /></Button></>}
                                    </div>
                                </div>
                            );
                        })}
                        </ReactSortable>
                    </section>
                ))}
                </ReactSortable>
                </div>

            </div>
            <Dialog open={!!draft} title={draft?.index === null ? 'Aggiungi sponsor' : 'Modifica sponsor'}
                onClose={() => { if (!saving) { setDraft(null); setError(''); } }}
                action={<Button disabled={!valid || saving} onClick={save}>{saving ? 'Salvataggio…' : 'Salva'}</Button>}>
                {draft && <div className={styles.form}>
                    <label>Categoria
                        <input list={categoryListId} value={draft.category} disabled={saving} placeholder="Es. Bronze Sponsor"
                            onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
                    </label>
                    <datalist id={categoryListId}>{categories.map((category) => <option key={category} value={category} />)}</datalist>
                    <label>Nome sponsor
                        <input value={draft.name} disabled={saving} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                    </label>
                    <label>URL immagine
                        <input type="url" value={draft.img} disabled={saving} placeholder="https://..."
                            onChange={(e) => setDraft({ ...draft, img: e.target.value })} />
                    </label>
                    {!saving && <Uploader preview={draft.img} multiple={false} type={FileType.Image} iconButtons
                        onComplete={(uploads) => setDraft((prev) => prev ? { ...prev, img: uploads?.[0]?.url || '' } : prev)}
                        onDelete={() => setDraft((prev) => prev ? { ...prev, img: '' } : prev)} />}
                    {error && <p role="alert">{error}</p>}
                </div>}
            </Dialog>
        </Card>
    );
}
